import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {FrtAccessorialsMasterService} from '../../services/frt-accessorials-master/frt-accessorials-master.service';
import {RateTypesService} from 'src/app/services/rate-types/rate-types.service';
import {
  FrtAcessorialsMaster,
  RatesSroMaster,
  RateTypes,
  StateCountry
} from 'src/app/shared/common-components/common-structure';
import {RateTypesSroService} from 'src/app/services/rate-types-sro/rate-types-sro.service';
import {RatesSroMasterService} from 'src/app/services/rates-sro-master/rates-sro-master.service';
import {RatesSroOriginDestinationService} from 'src/app/services/rates-sro-origin-destination/rates-sro-origin-destination.service';
import {StateCountryService} from 'src/app/services/state-country/state-country.service';
import {filter} from 'rxjs/operators';
import {LoadingService} from 'src/app/services/loading/loading.service';
import {AlertService} from '../../services/alert/alert.service';
import {CarrierService} from '../../services/carrier/carrier.service';
import {BehaviorSubject} from 'rxjs';
import {takeWhile} from 'rxjs';
import { NgFor, NgIf } from '@angular/common';

@Component({
    selector: 'app-frt-rate-global-update',
    templateUrl: './frt-rate-global-update.component.html',
    styleUrls: ['./frt-rate-global-update.component.css'],
    imports: [ReactiveFormsModule, NgFor, NgIf]
})
export class FrtRateGlobalUpdateComponent implements OnInit, OnDestroy {
  private observer$ = new BehaviorSubject(true);
  rateTypes: RateTypes[] = [];
  frtRateGlobalUpdateFormGroup: UntypedFormGroup;
  recordsCount: Number;
  showRecords: Boolean = false;
  showTl: boolean;
  showLtl: boolean;
  showVehcl: boolean;
  showAccl: boolean;
  ltlChangeType = [
    'Replace',
    'Increase by Percentage',
    'Decrease by Percentage'
  ];

  tlChangeType = [
    'Replace',
    'Add Amount',
    'Subtract Amount',
    'Increase by Percentage',
    'Decrease by Percentage'
  ];

  vehcleChangeType = [
    'Replace',
    'Add Amount',
    'Subtract Amount',
    'Increase by Percentage',
    'Decrease by Percentage'
  ];

  accessorials: any;
  ratesSroIdList: RatesSroMaster[] = [];
  sroOriginStateCountries: StateCountry[] = [];
  frtAccessorials: FrtAcessorialsMaster[] = [];
  carrierId: any;
  rateBidCyclesId: number;

  constructor(private formBuilder: UntypedFormBuilder,
              private rateTypesService: RateTypesService,
              private alertService: AlertService,
              private rateTypesSroService: RateTypesSroService,
              private ratesSroOriginDestinationService: RatesSroOriginDestinationService,
              private stateCountryService: StateCountryService,
              private ratesSroMasterService: RatesSroMasterService,
              public loader: LoadingService,
              private frtAccessorialsMasterService: FrtAccessorialsMasterService,
              private carrierService: CarrierService) {
    this.carrierService.getRateBidCycleId()
      .pipe(takeWhile(() => this.observer$.value)).subscribe((res: any) => {
      this.rateBidCyclesId = res.ratesBidcyclesId;
    });
  }

  /**
   * @description initializes the reactive form and gets the list of all accessorials from the master table
   */
  ngOnInit() {
    this.loader.show();
    this.carrierService.getCarrierObservable().pipe(takeWhile(() => this.observer$.value)).subscribe((carrier: any) => {
      this.carrierId = carrier.carrierId;
      this.loader.hide();
    });
    this.initializeFrtForm();
  }

  /**
   * @description listens to changes in the rate type form control and displays the content accordingly. hides and shows div elements according to the
   * rate type id selected and also gets the list of sro's based on rate type id.
   */
  onRateTypeChange(event) {
    this.getGlobalRecords();
    const rateTypeId = event.target.value;
    this.rateTypesSroService.getSroListByRateTypeIdByBidCycle(rateTypeId, this.rateBidCyclesId, this.carrierId)
      .pipe(takeWhile(() => this.observer$.value)).subscribe((ratesSroIdList: any) => {
      this.ratesSroIdList = ratesSroIdList;
      this.loader.hide();
    }, error => {
      this.loader.hide();
      this.alertService.error('Error occurred while retrieving Sro List. Please contact system administrator.');
    });
    if (rateTypeId === '18') {
      this.showTl = true;
      this.showLtl = false;
      this.showVehcl = false;
      this.showAccl = true;
    } else if (rateTypeId === '19') {
      this.showTl = false;
      this.showLtl = true;
      this.showVehcl = false;
      this.showAccl = true;
    } else {
      this.showTl = false;
      this.showLtl = false;
      this.showVehcl = true;
      this.showAccl = false;
    }
  }

  /**
   * @description listens to changes in the sro type form control and displays the content accordingly. gets the list of orgin state countries
   * and accessorial codes for the sro
   */
  onSroTypeChange(event) {
    this.getGlobalRecords();
    const sroMasterId = event.target.value;
    this.loader.show();
    this.ratesSroOriginDestinationService.getOriginStateCountryBySroMasterId(sroMasterId).pipe(takeWhile(() => this.observer$.value)).subscribe(stateCountries => {
      this.sroOriginStateCountries = stateCountries.filter((origin: any) => origin.businessType.toLowerCase().indexOf('frt') > -1);
      this.frtAccessorialsMasterService.getFrtAcessorialsMasterByCarrierId(this.carrierId, sroMasterId).pipe(takeWhile(() => this.observer$.value)).subscribe(
          (FrtAcessorials: any) => {
            this.frtAccessorials = FrtAcessorials;
            this.loader.hide();
          }, error => {
            this.alertService.error('Error while getting accessorial types. Please contact system administrator');
            this.loader.hide();
          });
    }, error => {
      this.loader.hide();
      this.alertService.error('Error occurred while getting origin list. Please contact system administrator.');
    });
  }

  /**
   * @description gets executed whenever user changes the origin and gets the no of available records to be updated.
   * @param $e: event
   */
  onOriginChange($e) {
    this.getGlobalRecords();
  }

  /**
   * @description calls the service to get no of global update records
   */
  getGlobalRecords() {
    this.showRecords = false;
    const rateTypeId = this.frtRateGlobalUpdateFormGroup.get('rateTypeId').value ? this.frtRateGlobalUpdateFormGroup.get('rateTypeId').value : -1;
    const sroMasterId = this.frtRateGlobalUpdateFormGroup.get('sroMasterId').value ? this.frtRateGlobalUpdateFormGroup.get('sroMasterId').value : -1;
    const originId = this.frtRateGlobalUpdateFormGroup.get('originId').value ? this.frtRateGlobalUpdateFormGroup.get('originId').value : -1;
    this.ratesSroOriginDestinationService.getFrtGlobalUpdateRecordsByOrigin(rateTypeId, this.carrierId, sroMasterId, originId, this.rateBidCyclesId)
      .pipe(takeWhile(() => this.observer$.value)).subscribe((res: any) => {
      this.showRecords = true;
      this.recordsCount = res;
    }, error => {
      this.alertService.error('Error while getting the no of records');
    });
  }

  /**
   * @description initializes the FRT form along with validations
   */
  initializeFrtForm() {
    this.frtRateGlobalUpdateFormGroup = this.formBuilder.group({
      rateTypeId: new UntypedFormControl('', Validators.required),
      sroMasterId: new UntypedFormControl('', Validators.required),
      originId: new UntypedFormControl('', Validators.required),
      tenderId: new UntypedFormControl('', [Validators.minLength(4), Validators.maxLength(4), Validators.pattern('^[a-zA-Z0-9]+$')]),
      ltlSurchargePct: new UntypedFormControl(''),
      ltlMinimumChargePct: new UntypedFormControl(''),
      ltlMinChargeChangeType: new UntypedFormControl(''),
      ltlSurchargePctChangeType: new UntypedFormControl(''),
      tlMinimumCharge: new UntypedFormControl('', [
        /*Validators.pattern('^-?[0-9]\\d*(\\.\\d{1,2})?$')*/
        Validators.pattern('^[1-9]\\d*(\\.\\d+)?$')
      ]),
      tlRateAmount: new UntypedFormControl('', [
        Validators.pattern('^[1-9]\\d*(\\.\\d+)?$')
      ]),
      tlMinChargeChangeType: new UntypedFormControl(''),
      tlRateAmountChangeType: new UntypedFormControl(''),
      vehcleRateAmountChangeType: new UntypedFormControl(''),
      vehcleRateAmount: new UntypedFormControl('', [
        Validators.pattern('^[1-9]\\d*(\\.\\d+)?$')]),
      accessorials: new UntypedFormArray([])
    }
    // { validator: this.atLeastOne(Validators.required) }
    );
  }

  resetTLOrLTLChange(change: string | (string | number)[], name: string | (string | number)[]) {
    this.frtRateGlobalUpdateFormGroup.get(change)
        .valueChanges.pipe(filter(value => value === '')).subscribe(val => {
      this.frtRateGlobalUpdateFormGroup.get(name).reset();
    });
  }

  // at lease one is required
  atLeastOne = (validator: ValidatorFn) => (
      group: UntypedFormGroup,
  ): ValidationErrors | null => {
    const hasAtLeastOne = group && group.controls && Object.keys(group.controls).some(k => (!validator(group.controls['ltlSurchargePctChangeType']) && !validator(group.controls['ltlSurchargePct']))
        || ( !validator(group.controls['ltlMinChargeChangeType']) && !validator(group.controls['ltlMinimumChargePct']))
        || ( !validator(group.controls['tlRateAmountChangeType']) && !validator(group.controls['tlRateAmount']))
        || ( !validator(group.controls['tlMinChargeChangeType']) && !validator(group.controls['tlMinimumCharge'])));
    return hasAtLeastOne ? null : { atLeastOne: true };
  }

  /*
   * @description getFormField for frtRateGlobalUpdateFormGroup
   */
  getFormField(field: string): any {
    return this.frtRateGlobalUpdateFormGroup.get(field);
  }

  /**
   * @description listen to changes in the type of operation performed.
   */
  onOperationChange() {
  }

  /**
   * @description adds accessorials form control into the form array
   */
  addAccessorials() {
    (this.frtRateGlobalUpdateFormGroup.get('accessorials') as UntypedFormArray).push(this.createNewAccessorial());
  }

  /**
   * @description creates accessorial form control in the form group array
   * @returns {FormGroup}: form group from HTML
   *  frtAccessorialMasterId
   */
  createNewAccessorial(): UntypedFormGroup {
    const formGroup = this.formBuilder.group({
      frtAccessorialMasterId: ['', [Validators.required]],
      accessorialChangeType: ['', [Validators.required]],
      accessorialAmount: ['', [Validators.pattern('^[1-9]\\d*(\\.\\d+)?$')]],
      minimumAmount: ['', [Validators.pattern('^[1-9]\\d*(\\.\\d+)?$')]]
    });
    return formGroup;
  }

  /**
   * @description removes accessorials form control from the form array at specified index
   * @param i: index of the form control
   */
  deleteAccessorials(i) {
    (this.frtRateGlobalUpdateFormGroup.get(
        'accessorials'
    ) as UntypedFormArray).removeAt(i);
  }

  /**
   * @description prepares payload and save the frt global update object.
   */
  save() {
    const payLoad = this.frtRateGlobalUpdateFormGroup.value;
    payLoad.carrierId = this.carrierId;
    payLoad.ratesBidcyclesId = this.rateBidCyclesId;
    payLoad.sroMasterIds = [parseInt(this.frtRateGlobalUpdateFormGroup.get('sroMasterId').value)];
    delete payLoad.sroMasterId;
    this.loader.show();
    this.frtAccessorialsMasterService.saveFrtGlobalUpdate(payLoad).subscribe();
    this.loader.hide();
    this.alertService.info('Your Global update request has been received. we will notify you the result of update via email.');
    this.frtRateGlobalUpdateFormGroup.reset();
    /*this.frtAccessorialsMasterService.saveFrtGlobalUpdate(payLoad).pipe(takeWhile(() => this.observer$.value)).subscribe(res => {
      this.loader.hide();
      this.alertService.success('Successfully updated rates for FRT globally.');
    }, (err: HttpErrorResponse) => {
      this.loader.hide();
      console.log('Backend returned status code: ', err.status);
      if (err.status === 200 || err.status === 201) {
        this.alertService.success('Successfully updated rates for FRT globally.');
      } else if (err.status === 404) {
        this.alertService.error('There was an error Updating the Records');
      } else {
        this.alertService.error(api_error); // show error.error, do not wait for success
      }
    });*/
  }

  ngOnDestroy() {
    this.observer$.next(false);
  }
}
